import { ActionMenu } from '@monorepo/shared/componentsV2/menus/ActionMenu';
import { useToast } from '@monorepo/shared/contexts';
import { WorkflowStepResponse } from 'mapistry-shared';
import React, { useMemo } from 'react';
import { useModal } from '../../../../shared/hooks/useModalV2';
import { useWorkflowStepDelete } from '../../hooks/useWorkflowStepDelete';
import { getStepDisplayInfo } from './stepUtils';

interface WorkflowStepActionsProps {
  onDeleteSuccess?: (deletedStepId: string) => void;
  organizationId: string;
  step: WorkflowStepResponse;
  openModalForExistingStep: (step: WorkflowStepResponse) => void;
  openPreviewStepPane: (step: WorkflowStepResponse) => void;
}

export function WorkflowStepActions({
  onDeleteSuccess,
  organizationId,
  step,
  openModalForExistingStep,
  openPreviewStepPane,
}: WorkflowStepActionsProps) {
  const { title } = getStepDisplayInfo(step);

  const { confirm } = useModal();

  const { showUserFriendlyErrorToast, success: showSuccess } = useToast();

  const [deleteStep] = useWorkflowStepDelete({
    config: {
      onError: (err) =>
        showUserFriendlyErrorToast(err, `Unable to delete ${title} step.`),
      onSuccess: () => {
        onDeleteSuccess?.(step.id);
        showSuccess(`${title} step has been deleted.`);
      },
    },
  });

  const actionMenuOptions = useMemo(
    () => [
      {
        displayName: 'Edit',
        onClick: () => openModalForExistingStep(step),
      },
      {
        displayName: 'Preview',
        onClick: () => openPreviewStepPane(step),
      },
      {
        displayName: 'Delete',
        onClick: async () => {
          await confirm({
            title: `Delete ${title} Step`,
            description: `Are you sure you want to delete this Workflow step? You cannot undo this action.`,
            confirmButtonText: 'Delete Workflow Step',
            afterConfirmButtonText: 'Deleting...',
            cancelButtonText: 'Keep Workflow Step',
            danger: true,
            onConfirmAsync: () =>
              deleteStep({
                organizationId,
                workflowId: step.workflowId,
                stepId: step.id,
              }),
          });
        },
      },
    ],
    [
      confirm,
      deleteStep,
      openModalForExistingStep,
      openPreviewStepPane,
      organizationId,
      step,
      title,
    ],
  );

  return (
    <ActionMenu
      altText={`Action menu for ${title} Step`}
      options={actionMenuOptions}
    />
  );
}
